import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn, ia } from 'lib/helpers/utility';
import { useResizeOnNextFrame } from 'lib/hooks/useResizeOnNextFrame';

import MoreMenu from '../MoreMenu/MoreMenu';
import { splitItemsToFit } from '../MoreMenu/lib/fitItems';
import HorizontalScroll from '../HorizontalScroll/HorizontalScroll';

import TabItem from './components/TabItem';
import ToggleItem from './components/ToggleItem';

/**
 * @param {import('./lib/propTypes').ToggleSwitchProps} props
 */
const ToggleSwitch = ({
  items = [],
  onClick = () => {},
  active,
  disabled = false,
  count,
  horizontalScroll = false,
  loading,
  className = '',
  // Size from the container, not the content, with a min-width of the first item plus More, so a
  // wrapping parent moves it to a new line instead of overlapping it, and picking never reflows.
  sizedByContainer = false,
  variant = 'pills'
}) => {
  const isTabs = variant === 'tabs';
  const Item = isTabs ? TabItem : ToggleItem;
  const slotClass = cn('flex shrink-0 gap-2', isTabs ? 'items-stretch' : 'items-center');
  const [selected, setSelected] = useState(active);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const itemWidthsRef = useRef([]);
  const moreWidthRef = useRef(0);
  const gapRef = useRef(0);
  const availableWidthRef = useRef(0);
  const [isMeasuring, setIsMeasuring] = useState(true);
  const [visibleItems, setVisibleItems] = useState([]);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [minWidth, setMinWidth] = useState(0);

  const activeItem = active ?? selected;

  const calculateVisibleItems = useCallback(() => {
    const widths = itemWidthsRef.current;
    if (widths.length !== items.length) return;
    const gap = gapRef.current;
    const moreWidth = moreWidthRef.current;
    setMinWidth(items.length > 1 ? widths[0] + gap + moreWidth : widths[0]);

    const split = splitItemsToFit({
      items,
      itemWidths: widths,
      availableWidth: availableWidthRef.current,
      gap,
      moreWidth,
      activeValue: activeItem
    });
    setVisibleItems(split.visibleItems);
    setHiddenItems(split.hiddenItems);
  }, [items, activeItem]);

  useLayoutEffect(() => {
    if (!horizontalScroll) setIsMeasuring(true);
  }, [horizontalScroll, items]);

  useLayoutEffect(() => {
    if (horizontalScroll || !isMeasuring || !measureRef.current) return;
    const slots = Array.from(measureRef.current.children);
    const moreSlot = slots.pop();
    itemWidthsRef.current = slots.map((el) => el.offsetWidth);
    moreWidthRef.current = moreSlot?.offsetWidth ?? 0;
    gapRef.current = parseFloat(getComputedStyle(measureRef.current).columnGap) || 0;
    availableWidthRef.current = containerRef.current?.offsetWidth ?? 0;
    setIsMeasuring(false);
  }, [horizontalScroll, isMeasuring, items]);

  useLayoutEffect(() => {
    if (horizontalScroll || isMeasuring) return;
    calculateVisibleItems();
  }, [horizontalScroll, isMeasuring, calculateVisibleItems]);

  const remeasure = useCallback(() => setIsMeasuring(true), []);
  useResizeOnNextFrame(containerRef, remeasure, { enabled: !horizontalScroll });

  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) setIsMeasuring(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = (item) => {
    if (disabled || item.value === activeItem) return;

    setSelected(item.value);
    onClick(item);
    item.onClick?.(item);
  };

  if (!ia(items)) return null;

  const renderMore = (onSelect) => (
    <>
      {!isTabs && <div className="h-5 w-px shrink-0 rounded-sm bg-neutral-200" />}
      <MoreMenu
        items={items}
        onSelect={onSelect}
        className={isTabs ? 'flex h-full shrink-0 items-center' : 'h-7'}
        buttonClassName={cn('!px-2', isTabs && 'text-sm font-500 hover:!bg-neutral-75')}
        textClassName={isTabs ? 'text-neutral-600' : undefined}
      />
    </>
  );

  if (horizontalScroll) {
    return (
      <HorizontalScroll
        className={cn('rounded-md', disabled && 'pointer-events-none cursor-not-allowed')}>
        {items.map((item) => (
          <ToggleItem
            key={item.value ?? item.label}
            item={item}
            active={active === item.value}
            disabled={disabled}
            onClick={handleClick}
            count={count}
            loading={loading}
          />
        ))}
      </HorizontalScroll>
    );
  }

  return (
    <div
      ref={containerRef}
      style={sizedByContainer ? { minWidth, contain: 'inline-size' } : undefined}
      className={cn(
        'relative flex w-full min-w-0 max-w-full gap-2',
        isTabs ? 'h-full items-stretch gap-1' : 'items-center',
        disabled && 'pointer-events-none cursor-not-allowed',
        className
      )}>
      {isMeasuring && (
        <div
          ref={measureRef}
          aria-hidden="true"
          className={cn('invisible flex gap-2', isTabs ? 'h-full gap-1' : 'items-center')}>
          {items.map((item) => (
            <div key={item.value ?? item.label} className={slotClass}>
              <Item item={item} active={false} onClick={() => {}} />
            </div>
          ))}
          <div className={slotClass}>{renderMore(() => {})}</div>
        </div>
      )}
      {!isMeasuring &&
        visibleItems.map((item) => (
          <div
            key={item.value ?? item.label}
            className={cn(
              slotClass,
              (activeItem === item.value || visibleItems.length === 1) && 'min-w-0 shrink'
            )}>
            <Item
              item={item}
              active={active === item.value}
              disabled={disabled || item.disabled}
              onClick={handleClick}
              count={count}
              loading={loading}
            />
          </div>
        ))}
      {!isMeasuring && hiddenItems.length > 0 && renderMore(handleClick)}
    </div>
  );
};

export default ToggleSwitch;
